import React, { useState, useEffect, useMemo } from "react"
import { useNavigate } from "react-router-dom"
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  FormGroup,
  Label,
  Input,
  InputGroup,
  InputGroupText,
  Badge,
} from "reactstrap"
import { Form } from "react-bootstrap"

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faEye, faEyeSlash } from "@fortawesome/free-solid-svg-icons"
import { Link } from "react-router-dom"
import { UncontrolledTooltip } from "reactstrap"
import toastr from "toastr"
import TableContainer from "../../components/Common/TableContainer"
import DeleteModal from "components/Common/DeleteModal"
import apiURl from "url"

const Users = () => {
  // Meta title
  document.title = "Tool Users"

  const navigate = useNavigate()

  // Authorization check
  const CheckLog = localStorage.getItem("role")
  if (CheckLog !== "Admin" && CheckLog !== "Sub-Admin") {
    navigate("/")
  }

  // State management
  const [showPassword, setShowPassword] = useState(false)
  const [users, setUsers] = useState([])
  const [accountModal, setAccountModal] = useState(false)
  const [deleteModal, setDeleteModal] = useState(false)
  const [deleteID, setDeleteID] = useState(null)
  const [editUser, setEditUser] = useState(null)
  const [formData, setFormData] = useState({
    fullname: "",
    email: "",
    phone: "",
    password: "",
    role: "",
    image: null,
  })

  // Toggle password visibility
  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword)
  }

  // Handle form input changes
  const handleInputChange = e => {
    const { name, value } = e.target
    setFormData(prev => ({ ...prev, [name]: value }))
  }

  const handleImageChange = e => {
    const file = e.target.files[0]
    if (file) {
      setFormData(prev => ({ ...prev, image: file }))
    }
  }

  // Fetch all users
  const fetchAllUsers = async () => {
    try {
      const response = await fetch(`${apiURl}/api/users/fetchUsers`)
      const json = await response.json()
      if (response.status === 200) setUsers(json.users.reverse())
    } catch (err) {
      console.error("Fetch Error:", err)
    }
  }

  // Fetch user data by ID for editing
  const fetchUserDataByID = async id => {
    try {
      const response = await fetch(`${apiURl}/api/users/fetchUserByID`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ id }),
      })
      const json = await response.json()
      if (response.status === 200 && json.status === "success") {
        const userData = json.UserDataByID[0]
        if (userData) {
          setFormData({
            fullname: userData.full_name || "",
            email: userData.email_ || "",
            phone: userData.phone_ || "",
            role: userData.role_ || "",
            image: userData.image_ || "",
            password: "", // Password not retrieved for security
          })
          setEditUser(userData)
          setAccountModal(true)
        }
      }
    } catch (err) {
      console.error("Fetch User Error:", err)
    }
  }

  // Handle save (create or update)
  const handleSaveUser = async e => {
    e.preventDefault()

    // Form validation
    if (!formData.fullname.trim()) return toastr.error("Full Name is required")
    if (!formData.email.trim()) return toastr.error("Email is required")
    if (!formData.phone.trim()) return toastr.error("Phone is required")
    if (!formData.role.trim()) return toastr.error("Role is required")
    if (!formData.password.trim() && !editUser)
      return toastr.error("Password is required")

    const method = editUser ? "PUT" : "POST"
    const url = editUser
      ? `${apiURl}/api/users/edit-user/${editUser.id}`
      : `${apiURl}/api/users/add-user`

    const formPayload = new FormData()
    formPayload.append("fullname", formData.fullname)
    formPayload.append("email", formData.email)
    formPayload.append("phone", formData.phone)
    formPayload.append("role", formData.role)
    formPayload.append("password", formData.password || "")

    if (formData.image) {
      formPayload.append("image", formData.image)
    }

    try {
      const response = await fetch(url, {
        method,
        body: formPayload, // no headers needed for FormData
      })

      const json = await response.json()

      if (response.ok) {
        toastr.success(editUser ? "User updated!" : "User added!")
        fetchAllUsers()
        setAccountModal(false)
        setEditUser(null)
        setFormData({
          fullname: "",
          email: "",
          phone: "",
          password: "",
          role: "",
          image: null,
        })
      } else {
        toastr.error(json.message || "Failed to save user")
      }
    } catch (err) {
      console.error("Save Error:", err)
      toastr.error("Failed to save user")
    }
  }

  // Handle delete
  const deleteUser = async () => {
    try {
      const response = await fetch(
        `${apiURl}/api/users/delete-user/${deleteID}`,
        {
          method: "DELETE",
        }
      )
      if (response.ok) {
        toastr.success("User deleted successfully")
        setDeleteModal(false)
        fetchAllUsers()
      } else {
        toastr.error("Failed to delete user")
      }
    } catch (err) {
      console.error("Delete Error:", err)
      toastr.error("Failed to delete user")
    }
  }

  // Initialize component
  useEffect(() => {
    fetchAllUsers()
  }, [])

  // Table columns
  const columns = useMemo(
    () => [
      {
        Header: "Full Name",
        accessor: "full_name",
        Cell: cellProps => <span>{cellProps.value}</span>,
      },
      {
        Header: "Email",
        accessor: "email_",
        Cell: cellProps => <span>{cellProps.value}</span>,
      },
      {
        Header: "Phone",
        accessor: "phone_",
        Cell: cellProps => <span>{cellProps.value}</span>,
      },
      {
        Header: "Role",
        accessor: "role_",
        Cell: cellProps => <span>{cellProps.value}</span>,
      },
      {
        Header: "Status",
        accessor: "status_",
        Cell: ({ row }) => {
          const isActive = row.original.status_ === "Active"
          const handleToggle = async () => {
            const newStatus = isActive ? "Not Active" : "Active"

            try {
              const res = await fetch(
                `${apiURl}/api/users/update-status/${row.original.id}`,
                {
                  method: "PUT",
                  headers: {
                    "Content-Type": "application/json",
                  },
                  body: JSON.stringify({ status_: newStatus }),
                }
              )

              if (res.ok) {
                toastr.success("Status updated successfully")
                fetchAllUsers()
              } else {
                toastr.error("Failed to update status")
              }
            } catch (err) {
              toastr.error("Server error")
              console.error(err)
            }
          }
          return (
            <Form>
              <Form.Check
                type="switch"
                id={`status-switch-${row.original.id}`}
                label={
                  isActive ? (
                    <Badge pill className="bg-success">
                      Active
                    </Badge>
                  ) : (
                    <Badge pill className="bg-danger">
                      Inactive
                    </Badge>
                  )
                }
                defaultChecked={isActive}
                onChange={handleToggle}
                style={{
                  color: isActive ? "green" : "red",
                  fontWeight: "bold",
                }}
              />
            </Form>
          )
        },
      },
      {
        Header: "Action",
        accessor: "id",
        Cell: cellProps => (
          <div className="d-flex gap-3">
            <Link
              to="#"
              onClick={() => fetchUserDataByID(cellProps.value)}
              className="text-primary"
            >
              <i
                className="mdi mdi-eye font-size-18"
                id={`viewtooltip-${cellProps.value}`}
              />
              <UncontrolledTooltip
                placement="top"
                target={`viewtooltip-${cellProps.value}`}
              >
                View
              </UncontrolledTooltip>
            </Link>
            <Link
              to="#"
              className="text-danger"
              onClick={() => {
                setDeleteID(cellProps.value)
                setDeleteModal(true)
              }}
            >
              <i
                className="mdi mdi-delete font-size-18"
                id={`deletetooltip-${cellProps.value}`}
              />
              <UncontrolledTooltip
                placement="top"
                target={`deletetooltip-${cellProps.value}`}
              >
                Delete
              </UncontrolledTooltip>
            </Link>
          </div>
        ),
      },
    ],
    []
  )

  return (
    <React.Fragment>
      <DeleteModal
        show={deleteModal}
        onDeleteClick={deleteUser}
        onCloseClick={() => setDeleteModal(false)}
      />

      <div className="page-content">
        <Container fluid>
          <Row>
            <Col xl="6">
              <h5 className="mt-2">Tool Users</h5>
            </Col>
            <Col>
              <div className="float-end">
                <Button
                  color="primary"
                  className="btn-sm w-md"
                  onClick={() => {
                    setFormData({
                      fullname: "",
                      email: "",
                      phone: "",
                      password: "",
                      role: "",
                      image: null,
                    })
                    setEditUser(null)
                    setAccountModal(true)
                  }}
                >
                  <i className="fa fa-fw fa-plus me-1" />
                  Create User
                </Button>
              </div>
            </Col>
          </Row>

          <Row className="pt-3">
            <Col lg="12">
              <Card>
                <CardBody>
                  <TableContainer
                    columns={columns}
                    data={users}
                    isGlobalFilter={true}
                    isAddOptions={false}
                    className="custom-table"
                    customPageSize={10}
                    FilterColumns={false}
                  />
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>

      {/* User Modal */}
      <Modal
        isOpen={accountModal}
        toggle={() => setAccountModal(!accountModal)}
        size="lg"
        centered
      >
        <Form onSubmit={handleSaveUser}>
          <ModalHeader toggle={() => setAccountModal(!accountModal)}>
            {editUser ? "Edit User" : "Create New User"}
          </ModalHeader>
          <ModalBody>
            <Row>
              <Col md={6}>
                <FormGroup>
                  <Label>Full Name *</Label>
                  <Input
                    type="text"
                    name="fullname"
                    value={formData.fullname}
                    onChange={handleInputChange}
                    placeholder="Enter full name"
                    required
                  />
                </FormGroup>

                <FormGroup>
                  <Label>Phone *</Label>
                  <Input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    placeholder="Enter phone number"
                    required
                  />
                </FormGroup>

                <FormGroup>
                  <Label>Password *</Label>
                  <InputGroup>
                    <Input
                      type={showPassword ? "text" : "password"}
                      name="password"
                      value={formData.password}
                      onChange={handleInputChange}
                      placeholder="Enter password"
                      required={!editUser}
                    />
                    <InputGroupText
                      onClick={togglePasswordVisibility}
                      style={{ cursor: "pointer" }}
                    >
                      <FontAwesomeIcon
                        icon={showPassword ? faEyeSlash : faEye}
                      />
                    </InputGroupText>
                  </InputGroup>
                </FormGroup>
              </Col>

              <Col md={6}>
                <FormGroup>
                  <Label>Email *</Label>
                  <Input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    placeholder="Enter email"
                    required
                  />
                </FormGroup>

                <FormGroup>
                  <Label>Role *</Label>
                  <Input
                    type="select"
                    name="role"
                    value={formData.role}
                    onChange={handleInputChange}
                    required
                  >
                    <option value="">Select Role</option>
                    <option value="Admin">Admin</option>
                    <option value="Sub-Admin">Sub-Admin</option>
                    <option value="Employee">Employee</option>
                  </Input>
                </FormGroup>
                <FormGroup>
                  <Label>Profile Image</Label>
                  <Input
                    type="file"
                    name="image"
                    accept="image/*"
                    onChange={handleImageChange}
                  />
                </FormGroup>

                {formData.image && (
                  <div className="mb-3">
                    <img
                      src={
                        typeof formData.image === "string"
                          ? `${apiURl}${formData.image}` // full image URL from server
                          : URL.createObjectURL(formData.image) // preview for new image
                      }
                      alt="Preview"
                      style={{
                        width: "70px",
                        height: "70px",
                        objectFit: "cover",
                        borderRadius: "5px",
                      }}
                    />
                  </div>
                )}
              </Col>
            </Row>
          </ModalBody>
          <ModalFooter>
            <Button
              color="secondary"
              onClick={() => {
                setFormData({
                  fullname: "",
                  email: "",
                  phone: "",
                  password: "",
                  role: "",
                  image: null,
                })
                setEditUser(null)
                setAccountModal(false)
              }}
            >
              Cancel
            </Button>
            <Button color="primary" type="submit">
              {editUser ? "Update User" : "Create User"}
            </Button>
          </ModalFooter>
        </Form>
      </Modal>
    </React.Fragment>
  )
}

export default Users
